import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { IInvoice } from '../../../../interfaces/invoices/IInvoice';
import { IInvoiceListState } from '../../../../interfaces/invoices/IInvoiceList';
import { InvoicesService } from './InvoicesApi';


const initialState: IInvoiceListState = {
    invoiceIsLoading: false,
    invoicesAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchInvoices = createAsyncThunk<any, IPaginationPayload>(
    'invoices/tryToFetchInvoices',
    async ({ pageNumber, pageSize, data }) => {
        const result = await InvoicesService.tryToFetchInvoices(pageNumber, pageSize, data);
        return result?.data;
    },
);

export const tryToFetchSingleInvoice = createAsyncThunk<IInvoice, string>(
    'invoices/tryToFetchSingleInvoice',
    async (id: string) => {
        const result = await InvoicesService.tryToFetchSingleInvoice(id);
        return result?.data?.data;
    },
);

export const tryToEditInvoice = createAsyncThunk<ApiResponse<IInvoice>, { id: string, data: any }>(
    'invoices/tryToEditInvoice',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToEditInvoice(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToSendEmailInvoice = createAsyncThunk<ApiResponse<IInvoice>, string>(
    'invoices/tryToSendEmailInvoice',
    async (id, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToSendEmailInvoice(id);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const invoicesSlice = createSlice({
    name: 'invoices',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchInvoices
            .addCase(tryToFetchInvoices.pending, (state) => {
                state.invoicesAreLoading = true;
            })
            .addCase(tryToFetchInvoices.rejected, (state) => {
                state.invoicesAreLoading = false;
            })
            .addCase(tryToFetchInvoices.fulfilled, (state, action) => {
                state.invoicesAreLoading = false;
                state.invoices = action.payload;
            })

            // tryToFetchSingleInvoice
            .addCase(tryToFetchSingleInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToFetchSingleInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
            })
            .addCase(tryToFetchSingleInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                state.invoiceDetails = action.payload;
            })

            // tryToEditInvoice
            .addCase(tryToEditInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToEditInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
            })
            .addCase(tryToEditInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                if (state.invoices?.data.elements)
                    state.invoices.data.elements =
                        { ...state }.invoices?.data.elements.map((invoice) => {
                            if (invoice?._id === action.meta?.arg?.id) {
                                return {
                                    ...invoice,
                                    invoiceInfo: {
                                        ...invoice?.invoiceInfo,
                                        shippingCosts: action?.meta?.arg?.data?.shippingCosts,
                                        totalAmount: Number(action?.meta?.arg?.data?.totalAmount || 0),
                                        total: (Number(action?.meta?.arg?.data?.shippingCosts || 0) + Number(action?.meta?.arg?.data?.totalAmount || 0))
                                    }
                                }
                            } else {
                                return invoice
                            }
                        }) || [];
            })

            // tryToSendEmailInvoice
            .addCase(tryToSendEmailInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToSendEmailInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
            })
            .addCase(tryToSendEmailInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                if (state.invoices?.data.elements)
                    state.invoices.data.elements =
                        { ...state }.invoices?.data.elements.map((invoice) => {
                            if (invoice?._id === action.meta?.arg) {
                                return {
                                    ...invoice,
                                    status: 'invoiced'
                                }
                            } else {
                                return invoice
                            }
                        }) || [];
            })
    },
});

export default invoicesSlice.reducer;